import api from "@/config/api";
import { useUser } from "@/context/user";
import { Organization } from "@/types/entities";
import { Forms } from "@/types/form";
import { getRoutes } from "@/utils/helpers";
import { log } from "@/utils/logger";
import storage from "@/utils/storage";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

const useSubmit = () => {
    const setLoading = useUser(state => state.setGlobalLoading);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const routes = getRoutes();

    return useMutation({
        mutationFn: async (state: {
            step1: Forms["OrganizationStep1"] | null;
            step2: Forms["OrganizationStep2"] | null;
        }) => {
            if (!state.step1 || !state.step2) return;

            const organization = {
                title: state.step1.title,
                industry: state.step1.industry,
                business: state.step1.business,
                companyId: state.step1.companyId,
                phone: state.step2.phone,
                email: state.step2.email,
                address: {
                    country: state.step1.country,
                    city: state.step2.city,
                    street: state.step2.street,
                    ...(state.step2.zipCode && { zipCode: state.step2.zipCode }),
                },
            };
            return await api.post<Organization>("/organizations", { data: { organization } });
        },
        onMutate: () => {
            setLoading(true);
        },
        onError: error => {
            log.error(error, "onSubmit");
        },
        onSuccess: data => {
            if (!data) return;

            log.success(`${data.title} has been successfully created.`);

            storage.setItem("oid", data._id);
            queryClient.setQueryData(["organization", data._id], data);

            navigate(routes.brands());
        },
        onSettled: () => {
            setLoading(false);
        },
    });
};

export { useSubmit };
